<template>
  <div class="decks-page-component">
    <img
      :src="displayAttachment && displayAttachment.largeThumbnail"
      alt=""
    />
    <asset-metadata :asset="deck" />
    <header-text
      :level="'h2'"
      class="deck-page-header"
    >
      {{ deck && deck.assetTitle }}
    </header-text>
    <template v-if="isSeries">
      <button
        v-if="deckStateStatus === states.NOT_STARTED"
        class="series-button begin-button"
        @click="$emit('begin-series')"
      >
        Begin
      </button>
      <button
        v-if="deckStateStatus === states.IN_PROGRESS"
        class="series-button continue-button"
        @click="$emit('continue-series')"
      >
        Continue
      </button>
      <button
        v-if="deckStateStatus !== states.NOT_STARTED"
        class="series-button reset-button"
        @click="$emit('restart-series')"
      >
        Restart Series
      </button>
    </template>
    <hlt-text
      v-if="deck.displayDescription"
      class="deck-page-description"
    >
      {{ deck.displayDescription }}
    </hlt-text>
    <header-text
      :level="'h3'"
      size="sm"
      class="deck-list-header"
    >
      {{ deckListHeader }}
    </header-text>
    <deck-asset-row
      v-for="card in cards"
      :key="card.id"
      :asset="cardAsset(card)"
      :card="card"
      :deck="deck"
      @card-row-clicked="$emit('card-row-clicked', card)"
    />
    <content-source-preview :content-source="contentSource" />
  </div>
</template>

<script>
import AssetMetadata from '@/components/ui/asset-metadata.vue';
import ContentSourcePreview from '@/components/content-sources/preview.vue';
import DeckAssetRow from '@/components/decks/assetRow.vue';
import HeaderText from '@/components/ui/header-text.vue';
import HltText from '@/components/ui/text.vue';

import CardMixin from '@/mixins/card';

const STATES = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export default {
  components: { AssetMetadata, ContentSourcePreview, DeckAssetRow, HeaderText, HltText },
  mixins: [CardMixin],
  props: {
    deck: {
      type: Object,
    },
  },
  data() {
    return {
      loading: true,
      states: STATES,
    };
  },
  computed: {
    cards() {
      if (this.deck) {
        return [...this.deck.cards].sort((a, b) => a.order - b.order);
      } else {
        return [];
      }
    },
    contentSource() {
      if (this.deck?.contentSourceId) {
        return this.$store.getters['contentSources/get'][this.deck.contentSourceId];
      }
    },
    deckListHeader() {
      return (this.deck.deckType === 'Standard') ? `${this.cards.length} Items` : `${this.cards.length} ${this.deck.deckType}`;
    },
    displayAttachment() {
      if (this.deck?.displayAttachmentId) {
        return this.$store.getters['attachments/get'][this.deck.displayAttachmentId];
      }
    },
    deckStateStatus() {
      if (this.deck && this.cards) {
        let inProgress;
        const completedCards = this.cards.filter(card => {
          const asset = this.cardAsset(card);
          let state;
          if (!asset || asset.assetType === 'Deck') {
            return;
          }
          if (asset.assetType === 'Grouping') {
            state = asset.states(this.$store);
          } else {
            state = asset.state(this.$store);
          }
          if (asset.assetType === 'Attachment') {
            const isComplete = asset.completedState(state);
            if (!isComplete && state?.currentTimestamp > 0) {
              inProgress = true;
            }
            return isComplete;
          } else {
            return asset.completedState(state);
          }
        });
        if (completedCards.length === 0 && !inProgress) {
          return STATES.NOT_STARTED;
        } else if (completedCards.length === this.cards.length && !inProgress) {
          return STATES.COMPLETED;
        } else {
          return STATES.IN_PROGRESS;
        }
      }
    },
    isSeries() {
      if (this.deck) {
        return (this.deck?.deckType === 'Episodes');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.decks-page-component {
  width: 100%;
  max-width: rem-calc(560);
  margin: rem-calc(64) auto 0;
  padding-bottom: rem-calc(64);

  img {
    object-fit: cover;
    object-position: center;
    width: 100%;
    border-radius: 4px;
  }

  .deck-page-header {
    margin: rem-calc(12) 0;
    font-size: rem-calc(24);
  }

  .deck-page-description {
    margin: 1.5rem 0;
  }

  .deck-list-header {
    margin: 2rem 0 rem-calc(12) 0;
    font-size: rem-calc(20);
  }

  .series-button {
    align-items: center;
    border-radius: 4px;
    background-color: #00B46D;
    border: 0;
    color: #ffffff;
    display: flex;
    font-weight: $semi-bold;
    height: rem-calc(40);
    justify-content: center;
    padding: 0;
    width: 100%;
    margin: 1rem 0 .5rem 0;
  }

  .reset-button {
    margin: 0;
    border: 1px solid #000000;
    background-color: transparent;
    color: #000000;
  }

  .image-container {
    border: 1px solid #E9E9E9;
    margin-bottom: 32px;
    position: relative;
    width: 100%;

    .image {
      object-fit: cover;
      object-position: center;
      width: 100%;
    }

    .expand-image-button {
      position: absolute;
      bottom: 24px;
      right: 24px;
      background: transparent;

      .expand-icon {
        height: 16px;
        width: 16px;
      }
    }
  }

  @include breakpoint(small) {
    margin: 0 auto;

    .media-attachment {
      margin: -2rem -2rem 0;
    }
  }
}
</style>
